/**
 * A page's content, rendered as what it is.
 *
 * The structure engine emits real tables — as HTML when it found cells, as pipe tables
 * when the whitespace reader recovered them — and dumping either as preformatted text
 * throws away the one thing the engine worked hardest to recover. This splits the page
 * into blocks and renders each in its own shape, while keeping the selected region's
 * reading highlighted and scrolled to wherever it lands.
 *
 * The HTML is parsed, not injected. Only `table`/`tr`/`td`/`th` and their `colspan` /
 * `rowspan` survive; everything else becomes text. Server-generated or not, HTML from a
 * document is content, and content does not get to run.
 */
import { useEffect, useMemo, useRef } from "react";

type Cell = { text: string; head: boolean; colSpan: number; rowSpan: number };
type Block =
  | { kind: "text"; text: string }
  | { kind: "table"; rows: Cell[][] };

const TABLE_HTML = /<table[\s\S]*?<\/table>/gi;

function parseHtmlTable(html: string): Cell[][] {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return [...doc.querySelectorAll("tr")].map((tr) =>
    [...tr.children]
      .filter((cell) => cell.tagName === "TD" || cell.tagName === "TH")
      .map((cell) => ({
        text: cell.textContent?.trim() ?? "",
        head: cell.tagName === "TH",
        colSpan: Number(cell.getAttribute("colspan") ?? 1) || 1,
        rowSpan: Number(cell.getAttribute("rowspan") ?? 1) || 1,
      })),
  );
}

/** A run of `| a | b |` lines, with the `| --- |` rule marking the row above as a header. */
function parsePipeTable(lines: string[]): Cell[][] {
  const cells = (line: string) =>
    line
      .replace(/^\s*\|/, "")
      .replace(/\|\s*$/, "")
      // a cell may contain an escaped pipe; only unescaped ones separate
      .split(/(?<!\\)\|/)
      .map((value) => value.replace(/\\\|/g, "|").trim());

  const isRule = (line: string) => /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/.test(line) && line.includes("-");
  const headerAt = lines.findIndex((row, index) => index > 0 && isRule(row));

  return lines
    .filter((_row, index) => index !== headerAt)
    .map((line, index) => {
      const head = headerAt !== -1 && index === 0;
      return cells(line).map((text) => ({ text, head, colSpan: 1, rowSpan: 1 }));
    });
}

function split(markdown: string): Block[] {
  const blocks: Block[] = [];

  // HTML tables first: they are self-delimiting, so everything between them is text.
  let at = 0;
  for (const match of markdown.matchAll(TABLE_HTML)) {
    const start = match.index ?? 0;
    if (start > at) blocks.push(...splitPipe(markdown.slice(at, start)));
    blocks.push({ kind: "table", rows: parseHtmlTable(match[0]) });
    at = start + match[0].length;
  }
  blocks.push(...splitPipe(markdown.slice(at)));
  return blocks.filter((block) => block.kind === "table" || block.text.trim().length > 0);
}

/** Within plain text, contiguous pipe rows are a table too. */
function splitPipe(text: string): Block[] {
  const blocks: Block[] = [];
  let run: string[] = [];
  let plain: string[] = [];

  const flushRun = () => {
    if (run.length >= 2) blocks.push({ kind: "table", rows: parsePipeTable(run) });
    else plain.push(...run);
    run = [];
  };
  const flushPlain = () => {
    if (plain.length) blocks.push({ kind: "text", text: plain.join("\n") });
    plain = [];
  };

  for (const line of text.split("\n")) {
    if (/^\s*\|.*\|\s*$/.test(line)) {
      flushPlain();
      run.push(line);
    } else {
      flushRun();
      plain.push(line);
    }
  }
  flushRun();
  flushPlain();
  return blocks;
}

function smooth(): ScrollBehavior {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
}

export function PageContentView({
  markdown,
  needle,
  className = "",
}: {
  markdown: string;
  /** The selected region's reading, highlighted wherever it lands. */
  needle: string | null;
  className?: string;
}) {
  const blocks = useMemo(() => split(markdown), [markdown]);
  const hit = useRef<HTMLElement>(null);
  const wanted = needle?.trim() ?? "";

  // Only the first match is marked, so the ref lands on exactly one node.
  const claimed = useRef(false);
  claimed.current = false;

  useEffect(() => {
    hit.current?.scrollIntoView({ behavior: smooth(), block: "center", inline: "nearest" });
  }, [wanted, markdown]);

  function mark(text: string, mono: boolean) {
    if (!wanted || claimed.current) return text;
    const at = text.indexOf(wanted);
    if (at === -1) return text;
    claimed.current = true;
    return (
      <>
        {text.slice(0, at)}
        <mark ref={hit} className={`bg-gold-surface text-on-gold ${mono ? "" : "px-0.5"}`}>
          {wanted}
        </mark>
        {text.slice(at + wanted.length)}
      </>
    );
  }

  if (!markdown.trim()) {
    return <div className={className}>Nothing on this page.</div>;
  }

  return (
    <div className={className}>
      {blocks.map((block, index) =>
        block.kind === "text" ? (
          <pre
            key={index}
            className="mb-3 font-mono text-[11.5px] leading-relaxed whitespace-pre-wrap text-dim"
          >
            {mark(block.text, true)}
          </pre>
        ) : (
          <div key={index} className="mb-4 overflow-x-auto border border-hair">
            <table className="w-full border-collapse text-[12px]">
              <tbody>
                {block.rows.map((row, r) => (
                  <tr key={r} className="even:bg-hair-soft">
                    {row.map((cell, c) =>
                      cell.head ? (
                        <th
                          key={c}
                          colSpan={cell.colSpan}
                          rowSpan={cell.rowSpan}
                          className="border border-hair bg-hair-soft px-2.5 py-1.5 text-start align-top font-mono text-[10.5px] font-bold tracking-[0.1em] text-text uppercase"
                        >
                          {mark(cell.text, false)}
                        </th>
                      ) : (
                        <td
                          key={c}
                          colSpan={cell.colSpan}
                          rowSpan={cell.rowSpan}
                          className="border border-hair px-2.5 py-1.5 align-top text-dim"
                        >
                          {mark(cell.text, false)}
                        </td>
                      ),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ),
      )}
    </div>
  );
}
